'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useTranslation } from 'react-i18next';

import { FonteTempoChip } from '@/app/ops/components/FonteTempoChip';
import { TAB_DIAGNOSTICO, temposHref } from '@/app/tempos/helpers/temposTabs';
import { SectionTitle } from '@/components/layout/SectionTitle';
import { SURFACE_CARD, SURFACE_INSET } from '@/components/surface/surfaces';
import { OpsFonteComposicao } from '@/domain/ops/interfaces/ops';
import { formatNumber } from '@/infrastructure/persistence/managers/helpers';
import { cn } from '@/lib/utils';

/** A tela virou aba de Tempos: o link vai direto, sem passar pela redireção. */
const CURADORIA_PATH = temposHref(TAB_DIAGNOSTICO);

interface HomeFontesCardProps {
  /** The same composition the Carteira shows, in the order the sources dispute it. */
  fontes: Array<OpsFonteComposicao>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * How much of the portfolio still has an estimated minute per piece: the same reading as the
 * Carteira's time source card, with the screen that chronometers it one click away.
 */
export function HomeFontesCard({ fontes, loading, failed, onRetry, className }: HomeFontesCardProps) {
  const { t } = useTranslation('common');

  return (
    <section className={cn(SURFACE_CARD, 'flex flex-col gap-2 p-4', className)}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <SectionTitle>{t('home.fontes.titulo')}</SectionTitle>
        <span className="text-xs leading-snug text-foreground-subtle">{t('home.fontes.subtitulo')}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size={6} color="foreground-faint" />
        </div>
      ) : failed ? (
        <NoDataFound
          variant="error"
          size="sm"
          title={t('home.erroTitulo')}
          description={t('home.fontes.erroDescricao')}
          action={{ label: t('home.tentar'), onClick: onRetry, icon: 'refresh' }}
        />
      ) : fontes.length === 0 ? (
        <NoDataFound
          variant="empty"
          size="sm"
          icon="schedule"
          title={t('home.fontes.vazioTitulo')}
          description={t('home.fontes.vazioDescricao')}
        />
      ) : (
        /* Two per row inside the block, so four sources read as a 2 x 2 grid. */
        <div className="flex animate-fade-in flex-wrap gap-2">
          {fontes.map((fonte) => (
            <div
              key={fonte.fonte}
              className={cn(
                SURFACE_INSET,
                'flex basis-full flex-col items-start gap-1 px-3 py-2 tablet:basis-[calc(50%_-_0.25rem)]',
              )}
            >
              <FonteTempoChip fonte={fonte.fonte} />
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold leading-none text-foreground">{formatNumber(fonte.ops)}</span>
                <span className="text-xs font-semibold text-foreground-muted">{t('ops.fontes.ops')}</span>
              </div>
              <span className="text-xs text-foreground-subtle">
                {t('ops.fontes.pecas', { pecas: formatNumber(fonte.pecas) })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pinned to the bottom: beside the taller day's list this block stretches, and the way
          out closes it instead of leaving the extra height blank. */}
      <LinkButton
        href={CURADORIA_PATH}
        variant="outlined"
        tone="neutral"
        size="sm"
        className="mt-auto w-fit normal-case"
      >
        <MaterialSymbol icon="fact_check" size={16} />
        {t('home.fontes.abrirCuradoria')}
      </LinkButton>
    </section>
  );
}
